import type {
  BreakpointID,
  BreakpointsMap,
  BreakpointsConfig,
} from '@packages/systems/style/types';
import {type Device, MediaQuery, MediaFeature, MediaType} from './media-query';
import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import {LARGER_BREAKPOINTS_CONFIG} from '@packages/systems/style/breakpoints-config';
import {cacheMemo} from '@packages/utilities/memo';

const createBreakpoint = (
  id: BreakpointID,
  config: BreakpointsConfig[keyof BreakpointsConfig]
) => {
  const mediaFeature =
    // @ts-expect-error - TS18048 - 'config' is possibly 'undefined'.
    config.minWidth === undefined
      ? // @ts-expect-error - TS18048 - 'config' is possibly 'undefined'. | TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
        MediaFeature.MaxWidth(config.maxWidth)
      : // @ts-expect-error - TS18048 - 'config' is possibly 'undefined'.
        MediaFeature.MinWidth(config.minWidth);

  return new Breakpoint(id, [new MediaQuery(MediaType.Screen, mediaFeature)]);
};

export class Breakpoint {
  id: BreakpointID;
  queries: Array<MediaQuery>;

  static XXL: Breakpoint;
  static XL: Breakpoint;
  static Large: Breakpoint;
  static Main: Breakpoint;
  static Medium: Breakpoint;
  static Small: Breakpoint;
  static Tiny: Breakpoint;

  constructor(id: BreakpointID, queries: Array<MediaQuery>) {
    this.id = id;
    this.queries = queries;
  }

  // Check whether this breakpoint is in effect for the device.
  isEffective(device: Device) {
    for (const query of this.queries) {
      if (!query.matches(device)) {
        return false;
      }
    }
    return true;
  }
}

Breakpoint.XXL = createBreakpoint(
  BREAKPOINT_ID_XXL,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_XXL]
);
Breakpoint.XL = createBreakpoint(
  BREAKPOINT_ID_XL,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_XL]
);
Breakpoint.Large = createBreakpoint(
  BREAKPOINT_ID_LARGE,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_LARGE]
);
Breakpoint.Main = createBreakpoint(
  BREAKPOINT_ID_MAIN,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_MAIN]
);
Breakpoint.Medium = createBreakpoint(
  BREAKPOINT_ID_MEDIUM,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_MEDIUM]
);
Breakpoint.Small = createBreakpoint(
  BREAKPOINT_ID_SMALL,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_SMALL]
);
Breakpoint.Tiny = createBreakpoint(
  BREAKPOINT_ID_TINY,
  LARGER_BREAKPOINTS_CONFIG[BREAKPOINT_ID_TINY]
);

const LARGE_BREAKPOINTS: Array<Breakpoint> = [
  Breakpoint.XXL,
  Breakpoint.XL,
  Breakpoint.Large,
];

const SMALL_BREAKPOINTS: Array<Breakpoint> = [
  Breakpoint.Medium,
  Breakpoint.Small,
  Breakpoint.Tiny,
];

const ALL_BREAKPOINTS: Array<Breakpoint> = [
  ...LARGE_BREAKPOINTS,
  Breakpoint.Main,
  ...SMALL_BREAKPOINTS,
];

export const ALL_BREAKPOINTS_MAP: Record<BreakpointID, Breakpoint> = {
  xxl: Breakpoint.XXL,
  xl: Breakpoint.XL,
  large: Breakpoint.Large,
  main: Breakpoint.Main,
  medium: Breakpoint.Medium,
  small: Breakpoint.Small,
  tiny: Breakpoint.Tiny,
};

export const LARGER_BREAKPOINTS: Array<Breakpoint> = ALL_BREAKPOINTS;

const isSmallBreakpoint = (breakpointId: BreakpointID) =>
  Boolean(
    SMALL_BREAKPOINTS.find((breakpoint) => breakpoint.id === breakpointId)
  );

const isLargeBreakpoint = (breakpointId: BreakpointID) =>
  Boolean(
    LARGE_BREAKPOINTS.find((breakpoint) => breakpoint.id === breakpointId)
  );

const memoBreakpoints = cacheMemo(ALL_BREAKPOINTS.length);

// We need to make sure breakpoints are in the correct order, otherwise we might
// apply the wrong media query first.
//
// Current implementation is not complete, following assumptions are made:
// - one breakpoint has only one media query
// - only minWidth and maxWidth are used
export const sortBreakpoints = (breakpoints: Breakpoint[]): Breakpoint[] =>
  [...breakpoints].sort(
    (breakpoint1: Breakpoint, breakpoint2: Breakpoint): number => {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const media1 = breakpoint1.queries[0].expression;
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const media2 = breakpoint2.queries[0].expression;

      if (media1.feature === media2.feature) {
        // When both breakpoints are minWidth, we sort ascending.
        // E.g. (min-width: 1440px) goes before (min-width: 1920px)
        if (media1.feature === 'minWidth') {
          return media1.value - media2.value;
        }
        // When both breakpoints are maxWidth, we sort descending.
        // E.g. (max-width: 991px) goes before (max-width: 767px)
        if (media1.feature === 'maxWidth') {
          return media2.value - media1.value;
        }
      } else {
        // When we compare minWidth with maxWith, we sort descending.
        // E.g. (min-width: 1920px) goes before (max-width: 991px)
        return media2.value - media1.value;
      }
      return 0;
    }
  );

/**
 * Used to convert data from the backend to breakpoints of Breakpoints class
 */
export const importBreakpoints = (breakpointsConfig: BreakpointsConfig) =>
  keyByBreakpoints(
    Object.keys(breakpointsConfig).map((breakpointId) =>
      createBreakpoint(
        breakpointId as BreakpointID,
        breakpointsConfig[breakpointId as BreakpointID]
      )
    )
  );

/**
 * Used to convert breakpoints to data accepted by backend
 */
export const exportBreakpoints = (
  breakpoints: Partial<Record<BreakpointID, Breakpoint>>
): BreakpointsConfig => {
  const exportableBreakpoints: BreakpointsConfig = {};
  for (const id in breakpoints) {
    const breakpoint: Breakpoint | undefined = breakpoints[id as BreakpointID];
    if (breakpoint instanceof Breakpoint) {
      // TODO: Handle multiple queries in a breakpoint.
      // Retrieve the single query expression we currently support for breakpoints.
      const expr = breakpoint.queries[0]?.expression;

      if (!expr) {
        throw new Error(
          'Unexpected breakpoint shape. Expected a single query expression.'
        );
      }

      let config;
      if (expr.feature === 'minWidth') {
        config = {minWidth: expr.value};
      }
      if (expr.feature === 'maxWidth') {
        config = {maxWidth: expr.value};
      }

      if (!config) {
        throw new Error(
          'Bad Breakpoint, expected feature to be either "minWidth" or "maxWidth".'
        );
      }

      exportableBreakpoints[id as BreakpointID] = config;
    }
  }
  return exportableBreakpoints;
};

export const keyByBreakpoints = (
  breakpoints: Array<Breakpoint>
): BreakpointsMap<Breakpoint> =>
  Object.freeze(
    sortBreakpoints(breakpoints).reduce((acc, breakpoint) => {
      acc[breakpoint.id] = breakpoint;
      return acc;
    }, {} as BreakpointsMap<Breakpoint>)
  );

/*
 * Get a sorted list of breakpoint IDs from a list of breakpoints.
 */
export const getOrderedBreakpointIds = (
  breakpoints: Breakpoint[] = ALL_BREAKPOINTS
): BreakpointID[] =>
  sortBreakpoints(breakpoints).map((breakpoint) => breakpoint.id);

/*
 * Given a breakpoint ID, returns a sorted list of breakpoint IDs depending on
 * the inheritance rules of the given argument.
 * - If one of the small breakpoint -> ['main', 'medium', 'small', 'tiny']
 * - If one of the large breakpoint -> ['main', 'large', 'xl', 'xxl']
 * - if 'main' breakpoint -> ['main', 'large', 'xl', 'xxl', 'medium', 'small', 'tiny'] (same as getOrderedBreakpointIds)
 */
export const getOrderedBreakpointIdsByInheritanceRules = memoBreakpoints(
  (breakpointId: BreakpointID = BREAKPOINT_ID_MAIN): BreakpointID[] => {
    if (isSmallBreakpoint(breakpointId)) {
      return getOrderedBreakpointIds([Breakpoint.Main, ...SMALL_BREAKPOINTS]);
    }
    if (isLargeBreakpoint(breakpointId)) {
      return getOrderedBreakpointIds([...LARGE_BREAKPOINTS, Breakpoint.Main]);
    }
    return getOrderedBreakpointIds(ALL_BREAKPOINTS);
  }
);

/*
 * Get all breakpoints, which will apply to the passed width.
 */
export const getEffectiveBreakpoints = (
  breakpointsMap: BreakpointsMap<Breakpoint>,
  {
    width,
  }: {
    width: number;
  }
): Breakpoint[] => {
  const breakpoints: Breakpoint[] = Object.values(breakpointsMap) as any;
  return breakpoints.filter((breakpoint) =>
    breakpoint.isEffective({
      width,
      mediaType: 'screen',
    })
  );
};

/*
 * Returns the most specific breakpoint from all effective breakpoints.
 * - relies on the keys order in `breakpointsMap`
 * - relies on the fact keys are not numbers, otherwise engines break sorting of the keys
 *
 * TODO LargerBreakpoints migrate `breakpointsMap` to use JavaScript Map https://github.com/webflow/webflow/issues/33115
 */
export const getMostSpecificBreakpoint = (
  breakpointsMap: BreakpointsMap<Breakpoint>,
  {
    width,
  }: {
    width: number;
  }
): // @ts-expect-error - TS2322 - Type 'Breakpoint | undefined' is not assignable to type 'Breakpoint'.
Breakpoint => getEffectiveBreakpoints(breakpointsMap, {width}).pop();
